import { useState } from "react";
import { Paperclip, FolderOpen, Upload, File, HardDrive, Link } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { uploadFileToObjectStorage } from "@/lib/object-storage-upload";

interface MediaFile {
  id: string;
  name: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  category: string;
  tags: string[];
}

interface DriveFile {
  id: string;
  driveFileId: string;
  name: string;
  mimeType: string;
  size: string;
  webViewLink: string;
  thumbnailLink: string;
}

export type EntityType = "task" | "note" | "calendarEvent" | "goal" | "sop";

/**
 * Upload a single file to the chosen destination and attach it to the entity.
 * Both destinations are two-step (create/upload, then attach); if the attach
 * step fails, the just-created resource is deleted (best-effort) so neither
 * the media library nor the user's Google Drive accumulates orphaned files.
 */
export async function uploadOneDocument(
  file: File,
  destination: "media" | "drive",
  entityType: EntityType,
  entityId: string,
) {
  if (destination === "media") {
    // Upload the file to object storage; the DB only stores the served URL,
    // keeping large files (e.g. videos) out of the database.
    const fileUrl = await uploadFileToObjectStorage(file);

    // Create the media library record pointing at the served URL
    const mediaRes = await apiRequest("POST", "/api/media", {
      name: file.name,
      fileName: file.name,
      fileUrl,
      fileType: file.type.split("/")[1] || "unknown",
      fileSize: file.size,
      category: "Documents",
      tags: [],
    });
    const mediaData = await mediaRes.json();

    // Then attach it to the entity
    const endpoint = {
      task: `/api/tasks/${entityId}/media`,
      note: `/api/notes/${entityId}/media`,
      calendarEvent: `/api/calendar/${entityId}/media`,
      goal: `/api/goals/${entityId}/media`,
      sop: `/api/sops/${entityId}/media`,
    }[entityType];

    try {
      await apiRequest("POST", endpoint, {
        mediaId: mediaData.id,
      });
    } catch (attachError) {
      // Attach failed after the media record was created — clean up the
      // just-created record so the media library doesn't accumulate
      // orphaned files the user never sees attached.
      try {
        await apiRequest("DELETE", `/api/media/${mediaData.id}`);
      } catch {
        // Best-effort cleanup; surface the original attach failure.
      }
      throw attachError;
    }

    return mediaData;
  } else {
    // Upload to Google Drive - convert file to base64 first
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

    // Extract base64 data (remove data:mime;base64, prefix)
    const base64Data = dataUrl.split(',')[1];

    // Upload to Google Drive
    const driveRes = await apiRequest("POST", "/api/google-drive/upload", {
      fileName: file.name,
      fileData: base64Data,
      mimeType: file.type || 'application/octet-stream',
    });
    const driveData = await driveRes.json();

    // Then attach it to the entity
    const endpoint = {
      task: `/api/tasks/${entityId}/drive`,
      note: `/api/notes/${entityId}/drive`,
      calendarEvent: `/api/calendar/${entityId}/drive`,
      goal: `/api/goals/${entityId}/drive`,
      sop: `/api/sops/${entityId}/drive`,
    }[entityType];

    try {
      await apiRequest("POST", endpoint, {
        driveFileId: driveData.driveFileId,
      });
    } catch (attachError) {
      // Attach failed after the file was uploaded to Drive — delete the
      // just-uploaded file so the user's Drive doesn't accumulate
      // orphaned files that never appear attached.
      try {
        await apiRequest("DELETE", `/api/google-drive/files/${driveData.driveFileId}`);
      } catch {
        // Best-effort cleanup; surface the original attach failure.
      }
      throw attachError;
    }

    return driveData;
  }
}

interface DocumentUploadButtonProps {
  entityType: EntityType;
  entityId: string;
  onAttachmentAdded?: () => void;
  /**
   * Only offer the "Enter URL" mode. Used by entities (e.g. goals) that only
   * have URL attachment endpoints, so unsupported media/Drive modes aren't
   * exposed.
   */
  urlOnly?: boolean;
}

export default function DocumentUploadButton({
  entityType,
  entityId,
  onAttachmentAdded,
  urlOnly = false
}: DocumentUploadButtonProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"main" | "media" | "drive" | "url">("main");
  const [urlName, setUrlName] = useState("");
  const [urlValue, setUrlValue] = useState("");
  const { toast } = useToast();
  // True after the user launches the Google Drive OAuth flow in a new tab
  // from this dialog; drives the "waiting for connection" polling + hint.
  const [connectingDrive, setConnectingDrive] = useState(false);

  // Fetch media files
  const { data: mediaFiles = [] } = useQuery<MediaFile[]>({
    queryKey: ["/api/media"],
    enabled: view === "media",
  });

  // Check whether Google Drive is connected before trying to list files, so
  // we can show a "connect Drive" hint instead of a misleading empty list.
  const { data: driveConnection, isLoading: driveConnectionLoading } = useQuery<{ connected: boolean }>({
    queryKey: ["/api/google-drive/connection"],
    enabled: view === "drive",
    // While the user is completing OAuth in another tab, poll so this dialog
    // flips to the file list on its own the moment the connection lands.
    refetchInterval: (query) =>
      connectingDrive && query.state.data?.connected !== true ? 3000 : false,
  });

  // Fetch drive files (only once we know a connection exists)
  const { data: driveFiles = [] } = useQuery<DriveFile[]>({
    queryKey: ["/api/google-drive/files"],
    enabled: view === "drive" && driveConnection?.connected === true,
  });

  // Mutation to attach media file
  const attachMediaMutation = useMutation({
    mutationFn: async ({ mediaId }: { mediaId: string }) => {
      const endpoint = {
        task: `/api/tasks/${entityId}/media`,
        note: `/api/notes/${entityId}/media`,
        calendarEvent: `/api/calendar/${entityId}/media`,
        goal: `/api/goals/${entityId}/media`,
        sop: `/api/sops/${entityId}/media`,
      }[entityType];

      return apiRequest("POST", endpoint, {
        mediaId,
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Document attached successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/${entityType}s/${entityId}/attachments`] });
      onAttachmentAdded?.();
      handleClose();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to attach document",
        variant: "destructive",
      });
    },
  });

  // Mutation to attach drive file
  const attachDriveMutation = useMutation({
    mutationFn: async ({ driveFileId }: { driveFileId: string }) => {
      const endpoint = {
        task: `/api/tasks/${entityId}/drive`,
        note: `/api/notes/${entityId}/drive`,
        calendarEvent: `/api/calendar/${entityId}/drive`,
        goal: `/api/goals/${entityId}/drive`,
        sop: `/api/sops/${entityId}/drive`,
      }[entityType];

      return apiRequest("POST", endpoint, {
        driveFileId,
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Document attached successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/${entityType}s/${entityId}/attachments`] });
      onAttachmentAdded?.();
      handleClose();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to attach document",
        variant: "destructive",
      });
    },
  });

  // Mutation to upload and attach file
  const uploadAndAttachMutation = useMutation({
    mutationFn: async ({ files, destination }: { files: File[]; destination: "media" | "drive" }) => {
      // Upload sequentially; keep going on failures and report per-file results.
      let succeeded = 0;
      let driveNotConnected = false;
      const failed: string[] = [];
      for (const file of files) {
        try {
          await uploadOne(file, destination);
          succeeded++;
        } catch (err) {
          failed.push(file.name);
          // The Drive upload endpoint returns 401 "Google Drive not connected"
          // when the tenant has no Drive connection. Every remaining file
          // would fail the same way, so stop and surface the real fix.
          if (
            destination === "drive" &&
            err instanceof Error &&
            err.message.includes("Google Drive not connected")
          ) {
            driveNotConnected = true;
            break;
          }
        }
      }
      return { succeeded, failed, driveNotConnected };
    },
    onSuccess: ({ succeeded, failed, driveNotConnected }, variables) => {
      if (succeeded > 0) {
        // Refresh consumers for everything that did make it through.
        queryClient.invalidateQueries({ queryKey: ["/api/media"] });
        queryClient.invalidateQueries({ queryKey: ["/api/google-drive/files"] });
        queryClient.invalidateQueries({ queryKey: [`/api/${entityType}s/${entityId}/attachments`] });
        onAttachmentAdded?.();
      }
      if (failed.length === 0) {
        toast({
          title: "Success",
          description: succeeded > 1
            ? `${succeeded} documents uploaded and attached successfully`
            : "Document uploaded and attached successfully",
        });
        handleClose();
      } else if (driveNotConnected) {
        toast({
          title: "Google Drive not connected",
          description: "Connect your Google Drive account in Settings before uploading files to Drive.",
          variant: "destructive",
        });
      } else if (succeeded > 0) {
        toast({
          title: "Partially attached",
          description: `${succeeded} attached; failed: ${failed.join(", ")}`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: `Failed to upload: ${failed.join(", ")}`,
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to upload and attach document",
        variant: "destructive",
      });
    },
  });

  const uploadOne = (file: File, destination: "media" | "drive") =>
    uploadOneDocument(file, destination, entityType, entityId);

  // Mutation to attach URL
  const attachUrlMutation = useMutation({
    mutationFn: async ({ name, url }: { name: string; url: string }) => {
      const endpoint = {
        task: `/api/tasks/${entityId}/url`,
        note: `/api/notes/${entityId}/url`,
        calendarEvent: `/api/calendar/${entityId}/url`,
        goal: `/api/goals/${entityId}/url`,
        sop: `/api/sops/${entityId}/url`,
      }[entityType];

      return apiRequest("POST", endpoint, {
        name,
        url,
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "URL link attached successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/${entityType}s/${entityId}/attachments`] });
      onAttachmentAdded?.();
      handleClose();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to attach URL link",
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    setOpen(false);
    setView("main");
    setUrlName("");
    setUrlValue("");
    setConnectingDrive(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      handleClose();
    } else {
      setOpen(true);
    }
  };

  const handleMediaSelect = (mediaId: string) => {
    attachMediaMutation.mutate({ mediaId });
  };

  const handleDriveSelect = (driveFileId: string) => {
    attachDriveMutation.mutate({ driveFileId });
  };

  const handleDirectUpload = (destination: "media" | "drive") => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '*/*'; // Accept all file types
    input.multiple = true; // Allow attaching one or many documents

    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files ?? []);
      if (files.length === 0) return;

      // Check file size (50MB limit per file)
      const MAX_SIZE = 50 * 1024 * 1024;
      const tooBig = files.find((f) => f.size > MAX_SIZE);
      if (tooBig) {
        toast({
          title: "Error",
          description: `"${tooBig.name}" exceeds the 50MB limit`,
          variant: "destructive",
        });
        return;
      }

      uploadAndAttachMutation.mutate({ files, destination });
    };

    input.click();
  };

  const handleBack = () => {
    setView("main");
  };

  const handleUrlSubmit = () => {
    if (!urlName.trim() || !urlValue.trim()) {
      toast({
        title: "Error",
        description: "Please enter both a name and URL",
        variant: "destructive",
      });
      return;
    }
    // Basic URL validation — only allow http and https schemes
    try {
      const parsed = new URL(urlValue);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        throw new Error("Unsafe protocol");
      }
    } catch {
      toast({
        title: "Error",
        description: "Please enter a valid URL starting with http:// or https://",
        variant: "destructive",
      });
      return;
    }
    attachUrlMutation.mutate({ name: urlName.trim(), url: urlValue.trim() });
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleButtonClick}
        data-testid="button-upload-document"
      >
        <Paperclip className="h-4 w-4 mr-2" />
        Attach Document
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange} modal={true}>
        <DialogContent
          className="max-w-xl max-h-[85vh] overflow-y-auto content-start"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.stopPropagation()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          {view === "main" && (
            <>
              <DialogHeader>
                <DialogTitle>Attach Document</DialogTitle>
                <DialogDescription>
                  Choose where to attach the document from
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-col gap-3 py-4">
                {!urlOnly && (
                <>
                <Button
                  variant="outline"
                  className="w-full h-16 text-left justify-start gap-4"
                  onClick={() => setView("media")}
                  data-testid="button-from-media"
                >
                  <FolderOpen className="h-6 w-6" />
                  <div>
                    <div className="font-medium">From Media Library</div>
                    <div className="text-sm text-muted-foreground">Choose from your uploaded files</div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="w-full h-16 text-left justify-start gap-4"
                  onClick={() => setView("drive")}
                  data-testid="button-from-drive"
                >
                  <HardDrive className="h-6 w-6" />
                  <div>
                    <div className="font-medium">From Google Drive</div>
                    <div className="text-sm text-muted-foreground">Choose from your Drive files</div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="w-full h-16 text-left justify-start gap-4"
                  onClick={() => handleDirectUpload("media")}
                  disabled={uploadAndAttachMutation.isPending}
                  data-testid="button-upload-direct"
                >
                  <Upload className="h-6 w-6" />
                  <div>
                    <div className="font-medium">Upload Directly</div>
                    <div className="text-sm text-muted-foreground">
                      {uploadAndAttachMutation.isPending ? "Uploading..." : "Pick one or more files and attach them"}
                    </div>
                  </div>
                </Button>
                </>
                )}

                <Button
                  variant="outline"
                  className="w-full h-16 text-left justify-start gap-4"
                  onClick={() => setView("url")}
                  data-testid="button-enter-url"
                >
                  <Link className="h-6 w-6" />
                  <div>
                    <div className="font-medium">Enter Online Document URL</div>
                    <div className="text-sm text-muted-foreground">Link to an external document or webpage</div>
                  </div>
                </Button>
              </div>
            </>
          )}

          {view === "media" && (
            <>
              <DialogHeader>
                <DialogTitle>Select from Media Library</DialogTitle>
                <DialogDescription>
                  Choose a file from your media library
                </DialogDescription>
              </DialogHeader>

              <div className="flex gap-2 mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBack}
                  data-testid="button-back-to-main"
                >
                  Back
                </Button>
              </div>

              <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                {mediaFiles.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No media files found. Upload some media first!
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {mediaFiles.map((media) => (
                      <Card
                        key={media.id}
                        className="cursor-pointer hover:border-primary transition-all p-3"
                        onClick={() => handleMediaSelect(media.id)}
                        data-testid={`media-item-${media.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <File className="h-8 w-8 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{media.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{media.fileName}</div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </>
          )}

          {view === "drive" && (
            <>
              <DialogHeader>
                <DialogTitle>Select from Google Drive</DialogTitle>
                <DialogDescription>
                  Choose a file from your Drive
                </DialogDescription>
              </DialogHeader>

              <div className="flex gap-2 mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBack}
                  data-testid="button-back-to-main"
                >
                  Back
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDirectUpload("drive")}
                  disabled={uploadAndAttachMutation.isPending}
                  data-testid="button-upload-to-drive"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadAndAttachMutation.isPending ? "Uploading..." : "Upload to Drive"}
                </Button>
              </div>

              <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                {driveConnectionLoading ? (
                  <div className="text-center py-8 text-muted-foreground" data-testid="text-drive-loading">
                    Checking Google Drive connection...
                  </div>
                ) : driveConnection?.connected !== true ? (
                  <div className="text-center py-8" data-testid="text-drive-not-connected">
                    <HardDrive className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                    <div className="font-medium mb-1">Google Drive not connected</div>
                    <div className="text-sm text-muted-foreground mb-4">
                      Connect your Google Drive account to browse and attach your Drive files.
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        // Run the OAuth flow in a new tab so this dialog stays
                        // open on the user's unfinished attachment; polling on
                        // the connection query flips it to the file list once
                        // the connection completes.
                        window.open("/api/google-drive/connect", "_blank", "noopener");
                        setConnectingDrive(true);
                      }}
                      data-testid="button-connect-drive"
                    >
                      <HardDrive className="h-4 w-4 mr-2" />
                      Connect Google Drive
                    </Button>
                    {connectingDrive && (
                      <div className="text-sm text-muted-foreground mt-3" data-testid="text-drive-connecting">
                        Finish connecting in the new tab — this dialog will update automatically.
                      </div>
                    )}
                  </div>
                ) : driveFiles.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No Drive files found.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {driveFiles.map((file) => (
                      <Card
                        key={file.id}
                        className="cursor-pointer hover:border-primary transition-all p-3"
                        onClick={() => handleDriveSelect(file.id)}
                        data-testid={`drive-item-${file.id}`}
                      >
                        <div className="flex items-center gap-3">
                          {file.thumbnailLink ? (
                            <img
                              src={file.thumbnailLink}
                              alt={file.name}
                              className="h-8 w-8 rounded object-cover"
                            />
                          ) : (
                            <File className="h-8 w-8 text-muted-foreground" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{file.name}</div>
                            <div className="text-xs text-muted-foreground">{file.size}</div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </>
          )}

          {view === "url" && (
            <>
              <DialogHeader>
                <DialogTitle>Enter Online Document URL</DialogTitle>
                <DialogDescription>
                  Add a link to an external document or webpage
                </DialogDescription>
              </DialogHeader>

              <div className="flex gap-2 mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBack}
                  data-testid="button-back-to-main"
                >
                  Back
                </Button>
              </div>

              <div className="flex flex-col gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="url-name">Display Name</Label>
                  <Input
                    id="url-name"
                    placeholder="e.g., Project Documentation"
                    value={urlName}
                    onChange={(e) => setUrlName(e.target.value)}
                    data-testid="input-url-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="url-value">URL</Label>
                  <Input
                    id="url-value"
                    type="url"
                    placeholder="https://..."
                    value={urlValue}
                    onChange={(e) => setUrlValue(e.target.value)}
                    data-testid="input-url-value"
                  />
                </div>
                <Button
                  onClick={handleUrlSubmit}
                  disabled={attachUrlMutation.isPending || !urlName.trim() || !urlValue.trim()}
                  data-testid="button-submit-url"
                >
                  {attachUrlMutation.isPending ? "Attaching..." : "Attach URL"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
